/**
 * Firebase configuration — shared across mobile and web.
 *
 * This file exports the Firebase config object and initialization helpers.
 * Each platform (mobile/web) has its own firebase.ts that calls initFirebase()
 * with the appropriate auth persistence.
 */

import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import { initializeAuth, getAuth, type Auth, type Persistence } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getFunctions, type Functions } from "firebase/functions";
import { PROD_HOSTNAMES } from "./hostnames";

export const firebaseConfig = {
  apiKey: "AIzaSyBorpzyeP8eJTnV279QIqj5sJuQjdjJfSo",
  authDomain: "eventify-data.firebaseapp.com",
  projectId: "eventify-data",
  storageBucket: "eventify-data.firebasestorage.app",
  messagingSenderId: "616533056652",
  appId: "1:616533056652:web:b54bb152b6302c7a22efa9",
  measurementId: "G-1KHQ3P8X2W",
};

// Multi-database routing. `(default)` = dev (current data). `prod` = launch DB.
// Web picks by hostname so one static bundle works on both therunup.ai and
// therunup-web.web.app. Mobile + SSR/Node pick by env var, baked at build time
// (EAS production profile sets EXPO_PUBLIC_FIREBASE_DB=prod; Cloud Run prod
// service sets NEXT_PUBLIC_FIREBASE_DB=prod). Unknown values default to
// `(default)` — never `prod` — so a spoof or misconfig can't silently write
// to the design partner's DB.
//
// PROD_HOSTNAMES is imported from ./hostnames.ts (single source of truth for
// all therunup.ai / therunup-* allowlists across web, mobile, and functions).

export type DatabaseId = "(default)" | "prod";

export function getDatabaseId(): DatabaseId {
  // Browser: hostname is the source of truth. SSR window is undefined and
  // falls through to env var.
  if (typeof window !== "undefined" && window.location?.hostname) {
    return PROD_HOSTNAMES.has(window.location.hostname) ? "prod" : "(default)";
  }
  // Node / SSR / mobile: env var baked at build time.
  //
  // IMPORTANT: must use the BARE `process.env.X` form — NO optional chaining,
  // NO `typeof process` guard, NO conditional. babel-preset-expo's inline
  // plugin ONLY matches the literal `MemberExpression process.env.EXPO_PUBLIC_*`
  // AST node. Any conditional access (`process.env?.X`, `process.env && process.env.X`,
  // `condition ? process.env.X : undefined`) DOES NOT match the plugin's
  // pattern, and the bundle keeps the runtime access — which on Hermes returns
  // `undefined` (process.env on RN is an empty shim except for inlined values).
  //
  // Caught 2026-05-27 by comparing bundle hashes with vs without env var set —
  // they were byte-identical, proving no inlining was happening. RN's process
  // shim is safe to call directly without a guard.
  //
  // NEXT_PUBLIC_X is for Next.js (web SSR). It is NOT inlined by babel-preset-expo;
  // on mobile it stays as `undefined` (mobile doesn't set this), so we fall
  // through to EXPO_PUBLIC_X. On Next.js SSR, NEXT_PUBLIC_X IS inlined by
  // webpack from Cloud Run env, taking precedence.
  const raw =
    process.env.NEXT_PUBLIC_FIREBASE_DB ||
    process.env.EXPO_PUBLIC_FIREBASE_DB ||
    "(default)";
  return raw === "prod" ? "prod" : "(default)";
}

export interface FirebaseInstances {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

// Attach a fetchImpl override that injects an X-Runup-Db header on every
// httpsCallable invocation. The Firebase Functions SDK calls this.fetchImpl
// for all callable POSTs (see @firebase/functions/dist/esm — class
// FunctionsService field). The server-side getDbForCallable() helper reads
// this header to route to the right Firestore database, which is what makes
// mobile callables DB-aware (mobile clients don't send Origin headers).
//
// Calling this from BOTH initFirebase() (mobile) and packages/web/src/lib/
// firebase.ts (web) is the single point that makes every callable route
// correctly, without touching the ~200 httpsCallable callsites scattered
// across the app.
export function attachDbHeaderInterceptor(functions: Functions, dbId: DatabaseId): void {
  type FetchLike = (
    url: string | URL,
    init?: RequestInit
  ) => Promise<Response>;
  const fns = functions as Functions & { fetchImpl?: FetchLike };
  const original: FetchLike =
    fns.fetchImpl ?? ((url: string | URL, init?: RequestInit) => fetch(url as RequestInfo, init));
  fns.fetchImpl = async (url, init) => {
    const headers = new Headers(init?.headers || {});
    headers.set("X-Runup-Db", dbId);
    return original(url, { ...(init || {}), headers });
  };
}

/**
 * Initialize Firebase with platform-specific auth persistence.
 *
 * Mobile: getReactNativePersistence(AsyncStorage)
 * Web:    browserLocalPersistence
 *
 * Idempotent: safe to call multiple times. The first call initializes; subsequent
 * calls return the existing instances. This is required because the same module
 * graph can reach this function via multiple paths (App.tsx's inline bootstrap +
 * src/config/firebase.ts re-exports + packages/web/src/lib/firebase.ts), and
 * Firebase's `initializeAuth` throws "auth/already-initialized" on a second call.
 */
export function initFirebase(
  persistence: Persistence,
  /**
   * Optional dbId override. Mobile callers MUST pass this — read from a file
   * under src/ (e.g., src/config/dbId.ts) where babel-preset-expo's inline
   * plugin actually transforms `process.env.EXPO_PUBLIC_*`. Reading the env
   * var inside getDatabaseId() doesn't work on mobile because this file lives
   * under packages/shared/ which the plugin doesn't transform. Verified
   * 2026-05-27 by bundle-hash diff. See src/config/dbId.ts comment block.
   *
   * Web callers don't pass this — they use hostname detection via
   * getDatabaseId(), which works because window.location.hostname is a
   * runtime value not requiring babel inlining.
   */
  dbIdOverride?: DatabaseId,
): FirebaseInstances {
  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

  let auth: Auth;
  try {
    auth = initializeAuth(app, { persistence });
  } catch (err) {
    const e = err as { code?: string };
    if (e?.code === "auth/already-initialized") {
      auth = getAuth(app);
    } else {
      throw err;
    }
  }

  const dbId = dbIdOverride ?? getDatabaseId();
  const db = dbId === "(default)" ? getFirestore(app) : getFirestore(app, dbId);
  const functions = getFunctions(app, "me-west1");
  attachDbHeaderInterceptor(functions, dbId);

  // Note: no client-side persistence is configured here. Mobile (React Native)
  // is the only caller of this function — the web app initializes Firestore
  // separately in packages/web/src/lib/firebase.ts with persistentLocalCache +
  // persistentMultipleTabManager (the new API). React Native does not support
  // IndexedDB, so the JS SDK's persistent cache cannot run there; the default
  // in-memory cache is what we get and is the right choice.
  //
  // History: previously called enableIndexedDbPersistence(db) here. That API
  // is deprecated and never actually enabled persistence on RN (returned
  // "unimplemented"). Removed 2026-05-24 to clean up the warning noise.

  return { app, auth, db, functions };
}
